import { useMemo } from 'react';
import { useAuthStore, StaffRole } from '@/stores/auth.store';
import { useGameStore } from '@/stores/game.store';

/**
 * Sections des Paramètres généraux (écrou du header), portées par l'URL /parametres/<section>.
 *
 * Deux familles, deux gardes :
 *   - les réglages du PANEL (fonctionnalités, admins, grades, projets, grades protégés) : admins
 *     globaux seulement, comme avant ;
 *   - les réglages du PROJET ACTIF (packs, fichiers, containers), venus de l'ancienne entrée
 *     Configuration > Paramètres : chacune garde le droit de grade qui l'ouvrait là-bas, sans
 *     exiger d'être admin. L'écrou s'affiche dès qu'une section est ouverte, et le menu des
 *     Paramètres ne montre que celles-là.
 *
 * Ce n'est que de la navigation : l'API reste juge de chaque requête.
 */
/** Réglages du panel entier : admins globaux uniquement. */
export const PANEL_SETTINGS_SECTIONS = ['fonctionnalites', 'admins', 'grades', 'projets', 'grades-proteges'] as const;
export type PanelSettingsSection = (typeof PANEL_SETTINGS_SECTIONS)[number];

/** Réglages du projet actif, ouverts par droit de grade (ancienne entrée Configuration > Paramètres). */
export const PROJECT_SETTINGS_SECTIONS = ['packs', 'fichiers', 'containers'] as const;
export type ProjectSettingsSection = (typeof PROJECT_SETTINGS_SECTIONS)[number];

export type SettingsSection = PanelSettingsSection | ProjectSettingsSection;

/** Toutes les sections, dans l'ordre du menu : la première ouverte est la cible de l'écrou. */
export const SETTINGS_SECTIONS: SettingsSection[] = [...PANEL_SETTINGS_SECTIONS, ...PROJECT_SETTINGS_SECTIONS];

export function isSettingsSection(raw: string | undefined | null): raw is SettingsSection {
  return !!raw && (SETTINGS_SECTIONS as string[]).includes(raw);
}

export function isPanelSettingsSection(raw: string | undefined | null): raw is PanelSettingsSection {
  return !!raw && (PANEL_SETTINGS_SECTIONS as readonly string[]).includes(raw);
}

export function isProjectSettingsSection(raw: string | undefined | null): raw is ProjectSettingsSection {
  return !!raw && (PROJECT_SETTINGS_SECTIONS as readonly string[]).includes(raw);
}

export function settingsPath(section: SettingsSection): string {
  return `/parametres/${section}`;
}

/** Section d'une URL des Paramètres (/parametres/fichiers → fichiers), ou null. */
export function settingsSectionOfPath(pathname: string): SettingsSection | null {
  const m = /^\/parametres\/([^/?#]+)/.exec(pathname);
  return m && isSettingsSection(m[1]) ? m[1] : null;
}

/**
 * Pages de config dont une seule suffit pour lire les packs du projet : miroir du droit générique
 * CONFIG_GROUP côté API (JwtAuthService), qui exclut Mondes (config.worlds n'édite aucun pack).
 * La section Pack n'a jamais eu de clé à elle : dans Configuration > Paramètres elle n'était gardée
 * par rien, et seuls ces grades pouvaient s'en servir (l'API refusait les autres). Elle reste donc
 * ouverte exactement à eux.
 */
const PACK_PAGES = [
  'config.blocks', 'config.items', 'config.entities', 'config.crafts', 'config.lootbox', 'config.jobs', 'config.recubejobs',
  'config.weapons', 'config.adminshop', 'config.factions', 'config.grades', 'config.cosmetics', 'config.files',
  'config.containers', 'config.requests',
];

/** Droit d'ouvrir une section, sans I/O. `hasPage` est celui du store d'auth (projet actif). */
export function canOpenSettingsSection(
  section: SettingsSection,
  isGlobalAdmin: boolean,
  hasPage: (page: string) => boolean,
): boolean {
  switch (section) {
    case 'packs':
      return PACK_PAGES.some((p) => hasPage(p));
    case 'fichiers':
      return hasPage('config.files');
    case 'containers':
      return hasPage('config.containers');
    default:
      return isGlobalAdmin;
  }
}

/**
 * Sections ouvertes à l'utilisateur pour le projet actif, dans l'ordre du menu. Vide : ni écrou ni
 * accès aux Paramètres. Se recalcule au changement de projet (les droits de grade sont par projet).
 */
export function useSettingsSections(): SettingsSection[] {
  const user = useAuthStore((s) => s.user);
  const hasPage = useAuthStore((s) => s.hasPage);
  const activeGameId = useGameStore((s) => s.activeGameId);
  return useMemo(() => {
    const isGlobalAdmin = !!user?.isAdmin || user?.role === StaffRole.ADMIN;
    return SETTINGS_SECTIONS.filter((s) => canOpenSettingsSection(s, isGlobalAdmin, hasPage));
    // activeGameId : hasPage lit le projet actif dans le store, la liste doit suivre un changement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasPage, activeGameId]);
}
